// Notion 스타일 통합 색 인라인 툴.
// 하나의 "색" 버튼을 누르면 [최근 사용 / 텍스트 색상 / 배경 색상] 메뉴가 뜨고,
// 항목을 고르면 선택 영역에 글자색(foreColor) 또는 배경색(hiliteColor)이 적용된다.

const RECENT_KEY = "atlas-editor-recent-colors";
const RECENT_MAX = 6;

// 텍스트 색 팔레트 (맨 앞 "기본" = 색 제거)
export const TEXT_PALETTE = [
  { name: "기본", clear: true },
  { name: "회색", value: "#787774" },
  { name: "갈색", value: "#976D57" },
  { name: "주황", value: "#CC782F" },
  { name: "노랑", value: "#C29343" },
  { name: "초록", value: "#548164" },
  { name: "파랑", value: "#487CA5" },
  { name: "보라", value: "#8A67AB" },
  { name: "분홍", value: "#B35488" },
  { name: "빨강", value: "#C4554D" },
];

// 배경(형광펜) 색 팔레트 (맨 앞 "기본" = 배경 제거)
export const BG_PALETTE = [
  { name: "기본", clear: true },
  { name: "회색", value: "#F1F1EF" },
  { name: "갈색", value: "#F4EEEE" },
  { name: "주황", value: "#FAEBDD" },
  { name: "노랑", value: "#FBF3DB" },
  { name: "초록", value: "#EDF3EC" },
  { name: "파랑", value: "#E7F3F8" },
  { name: "보라", value: "#F6F3F9" },
  { name: "분홍", value: "#FAF1F5" },
  { name: "빨강", value: "#FDEBEC" },
];

// 기본 버튼 아이콘: 메뉴의 "텍스트 색상" 항목 아이콘과 완전히 동일하게
// (같은 클래스 사용 → 네모 테두리 + 굵은 A, 기본 검정색)
const COLOR_BUTTON_ICON = '<span class="cdx-color-menu__icon">A</span>';

export class ColorInlineTool {
  static get isInline() {
    return true;
  }

  static get title() {
    return "색";
  }

  static get sanitize() {
    return {
      mark: { class: "cdx-marker", style: true },
      span: { class: true, style: true },
    };
  }

  constructor({ api }) {
    this.api = api;
    this.wrapper = null;
    this.button = null;
    this.menu = null;
    this.savedRange = null;
    this.open = false;
    this._onDocClick = null;
    this._state = false;
  }

  get state() {
    return this._state;
  }

  set state(state) {
    this._state = state;
    if (this.button) {
      this.button.classList.toggle(this.api.styles.inlineToolButtonActive, state);
    }
  }

  render() {
    const wrapper = document.createElement("div");
    wrapper.classList.add("cdx-color-tool");
    this.wrapper = wrapper;

    this.button = document.createElement("button");
    this.button.type = "button";
    this.button.classList.add(this.api.styles.inlineToolButton);
    this.button.innerHTML = COLOR_BUTTON_ICON;
    this.button.title = "글자/배경 색";

    this.menu = document.createElement("div");
    this.menu.classList.add("cdx-color-menu");
    this.menu.style.display = "none";
    // 메뉴 내부 상호작용이 선택 영역/툴바를 흔들지 않게 방지
    this.menu.addEventListener("mousedown", (event) => event.preventDefault());
    this.menu.addEventListener("click", (event) => event.stopPropagation());

    // menu 는 열 때 document.body 로 포탈한다(에디터의 stacking context/overflow 탈출).
    wrapper.appendChild(this.button);

    return wrapper;
  }

  // Editor.js 는 툴 버튼 클릭 시 surround(range) 호출.
  // 바로 적용하지 않고 선택 영역을 저장한 뒤 메뉴를 토글한다.
  surround(range) {
    if (!range || range.collapsed) {
      return;
    }
    this.savedRange = range.cloneRange();
    if (this.open) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    this.renderMenu();
    this.open = true;

    // body 로 포탈 + fixed 위치 → sticky Save 박스 등 어떤 stacking context 에도 가리지 않음
    if (this.menu.parentNode !== document.body) {
      document.body.appendChild(this.menu);
    }
    this.menu.style.position = "fixed";
    this.menu.style.display = "block";
    this.positionMenu();

    this._onDocClick = (event) => {
      const t = event.target;
      if (this.menu.contains(t) || (this.button && this.button.contains(t))) return;
      this.closeMenu();
    };
    this._onReposition = () => this.positionMenu();

    setTimeout(() => document.addEventListener("click", this._onDocClick), 0);
    window.addEventListener("scroll", this._onReposition, true);
    window.addEventListener("resize", this._onReposition);
  }

  positionMenu() {
    if (!this.button || !this.menu) return;
    const rect = this.button.getBoundingClientRect();
    const gap = 8;
    const margin = 8;
    const menuWidth = this.menu.offsetWidth || 190;
    const menuHeight = this.menu.offsetHeight || 0;

    let left = rect.left;
    if (left + menuWidth > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - margin - menuWidth);
    }

    // 기본은 버튼 아래. 아래 공간이 부족하면 위로 띄운다.
    let top = rect.bottom + gap;
    if (menuHeight && top + menuHeight > window.innerHeight - margin) {
      const above = rect.top - gap - menuHeight;
      top = above >= margin ? above : Math.max(margin, window.innerHeight - margin - menuHeight);
    }

    this.menu.style.top = `${top}px`;
    this.menu.style.left = `${left}px`;
  }

  closeMenu() {
    this.open = false;
    if (this.menu) {
      this.menu.style.display = "none";
      if (this.menu.parentNode === document.body) {
        document.body.removeChild(this.menu);
      }
    }
    if (this._onDocClick) {
      document.removeEventListener("click", this._onDocClick);
      this._onDocClick = null;
    }
    if (this._onReposition) {
      window.removeEventListener("scroll", this._onReposition, true);
      window.removeEventListener("resize", this._onReposition);
      this._onReposition = null;
    }
  }

  // 열 때마다 최근 사용 목록을 반영해 다시 그린다.
  renderMenu() {
    this.menu.innerHTML = "";

    const recents = loadRecents();
    if (recents.length) {
      this.menu.appendChild(buildSectionTitle("최근 사용"));
      recents.forEach((item) => this.menu.appendChild(this.buildItem(item)));
    }

    this.menu.appendChild(buildSectionTitle("텍스트 색상"));
    TEXT_PALETTE.forEach((item) =>
      this.menu.appendChild(this.buildItem({ kind: "text", ...item }))
    );

    this.menu.appendChild(buildSectionTitle("배경 색상"));
    BG_PALETTE.forEach((item) =>
      this.menu.appendChild(this.buildItem({ kind: "bg", ...item }))
    );
  }

  buildItem(item) {
    const row = document.createElement("button");
    row.type = "button";
    row.classList.add("cdx-color-menu__item");

    const icon = document.createElement("span");
    icon.classList.add("cdx-color-menu__icon");
    icon.textContent = "A";
    if (item.clear) {
      icon.classList.add("cdx-color-menu__icon--clear");
    } else if (item.kind === "text") {
      icon.style.color = item.value;
    } else {
      icon.style.backgroundColor = item.value;
    }

    const label = document.createElement("span");
    label.classList.add("cdx-color-menu__label");
    label.textContent = labelFor(item);

    row.appendChild(icon);
    row.appendChild(label);

    row.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.applyItem(item);
      this.closeMenu();
    });

    return row;
  }

  applyItem(item) {
    const range = this.savedRange;
    if (!range || range.collapsed) {
      return;
    }

    // 선택이 여러 블록(각기 다른 contenteditable)에 걸쳐 있으면 execCommand 는
    // 단일 편집영역에만 동작하므로, 수동 DOM 래핑으로 블록마다 적용한다.
    const startBlock = getContentEditableRoot(range.startContainer);
    const endBlock = getContentEditableRoot(range.endContainer);
    const crossBlock = !!startBlock && !!endBlock && startBlock !== endBlock;

    if (crossBlock) {
      if (item.kind === "text") {
        styleRangeAcrossBlocks(range, "color", item.clear ? null : item.value, item.clear);
      } else {
        styleRangeAcrossBlocks(range, "backgroundColor", item.clear ? null : item.value, item.clear);
      }
    } else {
      const selection = window.getSelection();
      if (!selection) {
        return;
      }

      selection.removeAllRanges();
      selection.addRange(range);

      document.execCommand("styleWithCSS", false, true);

      if (item.kind === "text") {
        const value = item.clear ? getClearTextValue(range) : item.value;
        document.execCommand("foreColor", false, value);
      } else {
        const value = item.clear ? "transparent" : item.value;
        document.execCommand("hiliteColor", false, value);
      }

      const root = getContentEditableRoot(range.commonAncestorContainer);
      if (root) {
        normalizeStyledMarkup(root);
      }
    }

    if (!item.clear) {
      pushRecent({ kind: item.kind, value: item.value, name: item.name });
    }

    queueMicrotask(() => this.checkState());
  }

  checkState() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      this.state = false;
      return;
    }
    const node = selection.anchorNode;
    const element = node?.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    const styled =
      element?.closest?.("span.cdx-text-color, mark.cdx-marker, span.cdx-marker") || null;
    this.state = Boolean(styled);
  }
}

/* ============================ 메뉴 헬퍼 ============================ */

function labelFor(item) {
  if (item.kind === "text") {
    return item.clear ? "기본" : item.name;
  }
  return item.clear ? "기본 배경" : `${item.name} 배경`;
}

function buildSectionTitle(text) {
  const title = document.createElement("div");
  title.classList.add("cdx-color-menu__section-title");
  title.textContent = text;
  return title;
}

function getClearTextValue(range) {
  const fallbackNode =
    range.startContainer?.nodeType === Node.TEXT_NODE
      ? range.startContainer.parentElement
      : range.startContainer;
  const inheritedColor = fallbackNode?.parentElement
    ? window.getComputedStyle(fallbackNode.parentElement).color
    : "";
  return inheritedColor || "#000000";
}

/* ==================== 여러 블록에 걸친 선택에 색/배경 적용 ==================== */

// range 경계의 텍스트 노드를 offset 에서 잘라, 선택 구간이 노드 경계에 딱 맞게 만든다.
// 그 뒤 선택에 걸친 각 텍스트 노드를 span 으로 감싸고, 영향받은 블록마다 정규화한다.
function styleRangeAcrossBlocks(range, styleProperty, value, isClear) {
  let sc = range.startContainer;
  let so = range.startOffset;
  let ec = range.endContainer;
  let eo = range.endOffset;

  // 시작 경계 분리
  if (sc.nodeType === Node.TEXT_NODE && so > 0 && so < sc.nodeValue.length) {
    const after = sc.splitText(so);
    if (ec === sc) {
      ec = after;
      eo -= so;
    }
    sc = after;
    so = 0;
  }
  // 끝 경계 분리 (ec 는 [0, eo) 부분만 남는다)
  if (ec.nodeType === Node.TEXT_NODE && eo > 0 && eo < ec.nodeValue.length) {
    ec.splitText(eo);
  }

  // 분리된 경계로 작업용 range 재구성
  const work = document.createRange();
  work.setStart(sc, so);
  if (ec.nodeType === Node.TEXT_NODE) {
    work.setEnd(ec, ec.nodeValue.length);
  } else {
    work.setEnd(ec, eo);
  }

  // 선택에 걸친 텍스트 노드 수집
  const nodes = [];
  const walkRoot = work.commonAncestorContainer;
  if (walkRoot.nodeType === Node.TEXT_NODE) {
    if (walkRoot.nodeValue && walkRoot.nodeValue.trim()) nodes.push(walkRoot);
  } else {
    const walker = document.createTreeWalker(walkRoot, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        return work.intersectsNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });
    let cur = walker.nextNode();
    while (cur) {
      nodes.push(cur);
      cur = walker.nextNode();
    }
  }

  const affectedRoots = new Set();

  nodes.forEach((textNode) => {
    const blockRoot = getContentEditableRoot(textNode);
    if (blockRoot) affectedRoots.add(blockRoot);

    const span = document.createElement("span");
    if (isClear) {
      // 초기화: 텍스트는 상속색으로, 배경은 transparent 로 (정규화 시 배경은 제거됨)
      if (styleProperty === "color") {
        const inherited = textNode.parentElement
          ? window.getComputedStyle(textNode.parentElement).color
          : "";
        span.style.color = inherited || "#000000";
      } else {
        span.style.backgroundColor = "transparent";
      }
    } else if (styleProperty === "color") {
      span.style.color = value;
    } else {
      span.style.backgroundColor = value;
    }

    textNode.parentNode.insertBefore(span, textNode);
    span.appendChild(textNode);
  });

  affectedRoots.forEach((root) => normalizeStyledMarkup(root));
}

function loadRecents() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((it) => it && it.value) : [];
  } catch (e) {
    return [];
  }
}

function pushRecent(item) {
  if (typeof window === "undefined" || !item?.value) return;
  const recents = loadRecents().filter(
    (it) => !(it.kind === item.kind && it.value === item.value)
  );
  recents.unshift(item);
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(recents.slice(0, RECENT_MAX)));
  } catch (e) {
    /* noop */
  }
}

/* ============================ 마크업 정규화 헬퍼 ============================ */

function getContentEditableRoot(node) {
  let current = node?.nodeType === Node.TEXT_NODE ? node.parentElement : node;

  while (current) {
    if (current.contentEditable === "true") {
      return current;
    }

    current = current.parentElement;
  }

  return null;
}

function normalizeStyledMarkup(root) {
  convertFontTags(root);

  const elements = root.querySelectorAll("span, mark");
  elements.forEach((element) => {
    syncStyleClasses(element);
    cleanupElement(element);
  });

  mergeAdjacentStyledNodes(root);
}

function convertFontTags(root) {
  const fonts = root.querySelectorAll("font[color]");

  fonts.forEach((font) => {
    const span = document.createElement("span");
    span.classList.add("cdx-text-color");
    span.style.color = normalizeColor(font.getAttribute("color"));

    while (font.firstChild) {
      span.appendChild(font.firstChild);
    }

    font.replaceWith(span);
  });
}

function syncStyleClasses(element) {
  const color = getNormalizedStyle(element, "color");
  const backgroundColor = getNormalizedStyle(element, "backgroundColor");

  if (color) {
    element.classList.add("cdx-text-color");
    element.style.color = color;
  } else {
    element.classList.remove("cdx-text-color");
    element.style.removeProperty("color");
  }

  if (backgroundColor && !isTransparent(backgroundColor)) {
    element.classList.add("cdx-marker");
    element.style.backgroundColor = backgroundColor;
  } else {
    element.classList.remove("cdx-marker");
    element.style.removeProperty("background-color");
  }
}

function cleanupElement(element) {
  if (element.tagName === "MARK" && !element.classList.contains("cdx-marker")) {
    unwrapElement(element);
    return;
  }

  if (!element.classList.length && !element.getAttribute("style")) {
    unwrapElement(element);
    return;
  }

  if (!element.textContent) {
    element.remove();
  }
}

function mergeAdjacentStyledNodes(root) {
  let changed = true;

  while (changed) {
    changed = false;
    const elements = root.querySelectorAll("span, mark");

    elements.forEach((element) => {
      const next = element.nextSibling;
      if (canMerge(element, next)) {
        while (next.firstChild) {
          element.appendChild(next.firstChild);
        }
        next.remove();
        changed = true;
      }
    });
  }
}

function canMerge(current, next) {
  return Boolean(
    next &&
      next.nodeType === Node.ELEMENT_NODE &&
      next.tagName === current.tagName &&
      current.className === next.className &&
      normalizeStyleText(current.getAttribute("style")) === normalizeStyleText(next.getAttribute("style"))
  );
}

function unwrapElement(element) {
  const parent = element.parentNode;
  if (!parent) {
    return;
  }

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }

  element.remove();
}

function getNormalizedStyle(element, styleProperty) {
  const cssValue = window.getComputedStyle(element).getPropertyValue(toCssProperty(styleProperty));
  const normalized = normalizeColor(cssValue);

  if (styleProperty === "backgroundColor" && isTransparent(normalized)) {
    return "";
  }

  return normalized;
}

function normalizeStyleText(styleText) {
  return (styleText || "")
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .sort()
    .join(";");
}

function toCssProperty(styleProperty) {
  return styleProperty === "backgroundColor" ? "background-color" : "color";
}

function isTransparent(color) {
  return color === "" || color === "transparent" || color === "#00000000" || color === "rgba(0,0,0,0)";
}

function normalizeColor(color) {
  if (!color) {
    return "";
  }

  if (color.startsWith("#")) {
    if (color.length === 4) {
      return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`.toLowerCase();
    }

    return color.toLowerCase();
  }

  const rgba = color.match(/\d+(\.\d+)?/g);
  if (!rgba || rgba.length < 3) {
    return color.toLowerCase().replace(/\s+/g, "");
  }

  if (rgba.length >= 4 && Number(rgba[3]) === 0) {
    return "rgba(0,0,0,0)";
  }

  const [red, green, blue] = rgba.slice(0, 3).map((value) => Number.parseInt(value, 10).toString(16).padStart(2, "0"));

  return `#${red}${green}${blue}`;
}
